"use client";

import React from "react";
import { EXIT_VARIANTS, type ExitVariant } from "./states";

const FRAMES = {
  "iPhone SE": { w: 375, h: 667, scale: 0.55 },
  "iPhone 15": { w: 393, h: 852, scale: 0.62 },
  iPad: { w: 810, h: 1080, scale: 0.42 }
} as const;
type FrameName = keyof typeof FRAMES;

const PLATFORMS: { label: string; tone: string }[] = [
  { label: "Mobile web", tone: "bg-gray-100 text-gray-600" },
  { label: "Native iOS", tone: "bg-blue-50 text-blue-700" },
  { label: "Native iPad", tone: "bg-blue-50 text-blue-700" },
  { label: "Native Android", tone: "bg-green-50 text-green-700" }
];

export default function FotoExitDevPage() {
  return (
    <div className="min-h-screen bg-slate-200 p-6">
      <div className="max-w-7xl mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Foto exit · camera-side &quot;Afsluiten&quot; flow
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            De afsluit-affordance op het camera-toestel: kleine link in de
            sticky header, een lichte confirm-sheet, en een aparte landing op{" "}
            <code>/foto/afgesloten</code> die per inlog-status aangepaste
            tekst en CTA toont. De iPhone-15 iframe rendert de echte
            componenten met mock-data.
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {PLATFORMS.map((p) => (
              <span
                key={p.label}
                className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${p.tone}`}
              >
                {p.label}
              </span>
            ))}
            <span className="text-[11px] text-gray-500 px-1">
              UI is identiek op alle platforms; PostHog-events taggen
              automatisch (<code>app_platform</code>, <code>surface</code>).
            </span>
          </div>
        </header>

        <section className="mb-10">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Alle variants · iPhone 15 viewport
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {EXIT_VARIANTS.map((v) => (
              <VariantCard key={v.key} variant={v} frame="iPhone 15" />
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Cross-device sizing · header link
          </h2>
          <p className="text-sm text-gray-600 mb-4 max-w-3xl">
            De link mag niet domineren op kleine schermen of verloren raken
            op tablets. Hieronder dezelfde header-state op drie viewports.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(["iPhone SE", "iPhone 15", "iPad"] as FrameName[]).map((f) => (
              <VariantCard
                key={f}
                variant={{
                  key: "gallery_idle_chat",
                  title: f,
                  desc:
                    f === "iPad"
                      ? "Tablet — ruim, link blijft compact aan de rand."
                      : f === "iPhone SE"
                        ? "Klein scherm — link houdt minimum tap-target."
                        : "Standaard mobiel — referentielayout."
                }}
                frame={f}
              />
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Cross-device sizing · confirm sheet
          </h2>
          <p className="text-sm text-gray-600 mb-4 max-w-3xl">
            Sheet is bottom-anchored met max-w-md, zodat de actie ook op
            tablets in een leesbare breedte valt zonder full-width te
            worden.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(["iPhone SE", "iPhone 15", "iPad"] as FrameName[]).map((f) => (
              <VariantCard
                key={f}
                variant={{
                  key: "confirm_default",
                  title: f,
                  desc:
                    f === "iPad"
                      ? "Sheet centreert, blijft compact aan onderkant."
                      : f === "iPhone SE"
                        ? "Korte schermhoogte — copy + acties passen zonder scroll."
                        : "Standaard mobiel."
                }}
                frame={f}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function VariantCard({
  variant,
  frame
}: {
  variant: ExitVariant;
  frame: FrameName;
}) {
  const { w, h, scale } = FRAMES[frame];
  return (
    <div className="bg-white rounded-xl shadow p-4 flex flex-col">
      <div className="mb-3">
        <h3 className="font-semibold text-gray-900 text-sm">
          {variant.title}
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">{variant.desc}</p>
      </div>
      <div
        className="mx-auto rounded-[32px] overflow-hidden border-4 border-gray-800 shadow-xl bg-black"
        style={{ width: w * scale, height: h * scale }}
      >
        <iframe
          src={`/dev/foto-exit/preview?v=${variant.key}`}
          title={variant.title}
          className="border-0 origin-top-left bg-white"
          style={{
            width: w,
            height: h,
            transform: `scale(${scale})`
          }}
        />
      </div>
    </div>
  );
}
